import { withStyles, Grid, Typography, Paper } from '@material-ui/core';
import React from 'react'; // eslint-disable-line
import styles, { StyledComponent } from '../../../../../styles/styles';
import { LabeledValue } from '../../../../../controls/labeled-value';
import { connect } from 'react-redux';
import { State } from '../../../../../redux/root-reducer';
import { FormDetails, getFormDetails } from '../form-details-reducer';
import { OpenModal, openModalAction } from '../../../../../form-modal/actions';
import { UpdateFormDetails, updateFormDetailsAction } from '../actions/update-form-details';
import { getClaim } from '../../../../../claim';
import { Nullable } from '../../../../../shared-types';
import { ClaimType } from '../../../../../claim/claim-types';

interface BaseProps {
    formDetails?: FormDetails;
    updateFormDetails?: UpdateFormDetails;
    disableEditButton?: boolean;
    type: string;
    claimType?: Nullable<ClaimType>;
}
interface Props extends BaseProps, StyledComponent {
    readonly openModal: OpenModal;
}

const addressDetailsView: React.FunctionComponent<Props> = ({
    classes,
    formDetails,
    type,
    claimType,
}: Props) => {
    const isLifeAssuredAddress = type === 'LifeAssured';
    const isRetrenchmentClaim = claimType === ClaimType.Retrenchment;
    let addressDetails;
    let entityAddress;
    let companyAddressDetails: any;

    if (formDetails) {
        addressDetails = isLifeAssuredAddress
            ? formDetails.lifeAssured?.addressDetails
            : formDetails.policyHolder?.individual?.addressDetails;

        entityAddress = formDetails.policyHolder?.individual?.companyDetails;
        companyAddressDetails = formDetails.policyHolder?.individual?.addressDetails;
    }

    const isThePolicyHolderAnIndividualOrCompany =
        formDetails?.policyHolder?.isThePolicyHolderAnIndividualOrCompany ||
        formDetails?.policyHolder?.policyHolderType;
    const isCompanyPolicyHolder = isThePolicyHolderAnIndividualOrCompany === 'Company';
    let header = !DreadOrTerminal() ? 'Residential Details' : 'Address Details';
    if (!isLifeAssuredAddress && isCompanyPolicyHolder) {
        header = 'Business Address';
    }

    const getSuburbTownValues = (currentAddress: any) =>
        isRetrenchmentClaim
            ? [currentAddress?.suburb, currentAddress?.town]
            : [currentAddress?.addressSuburb, currentAddress?.addressTown || currentAddress?.city];

    const addressDetailsValues = addressDetails
        ? [
              'RSA Street Address',
              addressDetails?.addressLineOne,
              addressDetails?.addressLineTwo,
              ...getSuburbTownValues(addressDetails),
              addressDetails?.postalCode,
              addressDetails?.province,
              addressDetails?.country,
          ]
        : [];

    const hasEntityBusinessAddress =
        !!entityAddress &&
        !!(
            entityAddress?.entityAddressLine1 ||
            entityAddress?.entityAddressLine2 ||
            entityAddress?.entitySuburb ||
            entityAddress?.entityTown
        );
    const businessAddress = hasEntityBusinessAddress
        ? [
              'RSA Street Address',
              entityAddress?.entityAddressLine1,
              entityAddress?.entityAddressLine2,
              entityAddress?.entitySuburb,
              entityAddress?.entityTown,
              entityAddress?.entityPostalCode,
              entityAddress?.entityProvince,
              companyAddressDetails?.country,
          ]
        : [
              'RSA Street Address',
              companyAddressDetails?.addressLineOne,
              companyAddressDetails?.addressLineTwo,
              ...getSuburbTownValues(companyAddressDetails),
              companyAddressDetails?.postalCode,
              companyAddressDetails?.province,
              companyAddressDetails?.country,
          ];

    const addressValue =
        !isLifeAssuredAddress && isCompanyPolicyHolder && claimType !== ClaimType.Disability
            ? businessAddress
            : addressDetailsValues;

    return (
        <React.Fragment>
            <Grid container justify="space-between" className={classes.marginTop2}>
                <Grid container item xs={10} alignItems="center">
                    <Typography style={{ padding: 10 }} className={classes.heading} variant="body1">
                        {header}
                    </Typography>
                </Grid>
            </Grid>
            <Paper className={classes.paper}>
                <Grid container justify="flex-start">
                    <Grid item xs={12} container justify="flex-start">
                        <Grid item xs={6} lg={6}>
                            <LabeledValue
                                label={
                                    !isLifeAssuredAddress && isCompanyPolicyHolder
                                        ? 'Business Address'
                                        : claimType === ClaimType.Disability
                                        ? 'Residential Details'
                                        : 'Residential Address'
                                }
                                value={addressValue
                                    .filter(Boolean) // removes null, undefined, empty string
                                    .join('\n')}
                            />
                        </Grid>
                        {DreadOrTerminal() && !isCompanyPolicyHolder && (
                            <Grid item xs={6} lg={6}>
                                <LabeledValue
                                    label="Postal Address"
                                    value={
                                        addressDetails?.sameAsResidentialAddress
                                            ? 'Same as residential'
                                            : `${
                                                  !addressDetails ||
                                                  addressDetails?.postalAddressLineOne === null
                                                      ? ''
                                                      : addressDetails?.postalAddressLineOne
                                              } ${'\n'} ${
                                                  !addressDetails ||
                                                  addressDetails?.postalAddressLineTwo === null
                                                      ? ''
                                                      : addressDetails?.postalAddressLineTwo
                                              } ${'\n'} ${
                                                  !addressDetails ||
                                                  addressDetails?.postalAddressSuburb === null
                                                      ? ''
                                                      : addressDetails?.postalAddressSuburb
                                              }  ${'\n'} ${
                                                  !addressDetails ||
                                                  addressDetails?.postalPostalCode === null
                                                      ? ''
                                                      : addressDetails?.postalPostalCode
                                              }  ${'\n'} ${
                                                  !addressDetails ||
                                                  addressDetails?.country === null
                                                      ? ''
                                                      : addressDetails?.country
                                              }`
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </React.Fragment>
    );
    function DreadOrTerminal() {
        return claimType === ClaimType.Dread || claimType === ClaimType.TerminalIllness;
    }
};

const mapStateToProps = (state: State, ownProps: BaseProps) => {
    const claim = getClaim(state);
    return {
        formDetails: ownProps.formDetails || getFormDetails(state),
        disableEditButton: ownProps.disableEditButton || false,
        claimType: claim.claimType,
    };
};

const mapDispatchToProps = {
    openModal: openModalAction,
    updateFormDetails: updateFormDetailsAction,
};

export const AddressDetailsView = connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(addressDetailsView));
